// Descarga de la evidencia de una alerta como archivo de video, ARMADA EN EL
// NAVEGADOR.
//
// Por qué existe: el backend solo entrega hoy el `manifest.json` de lo
// conservado (ADR-025 §5); el MP4 de verdad es `BE-56` (#110), que no existe.
// El dueño pidió (2026-09-20) un puente en el cliente mientras tanto, aunque
// contradice su decisión del 2026-09-13 de esperar a `BE-56`. Cuando `BE-56`
// exista, este módulo se retira y el botón vuelve a pedir `download-url`.
//
// Todo lo de aquí es PURO o recibe sus dependencias — no toca `fetch`, ni
// Firebase, ni `mux.js` directamente — para poder ejecutarlo en Node contra
// segmentos `.ts` reales. El cableado con la red está en `lib/api/evidenceClip.ts`.

const MPEG_TS_MIME = 'video/mp2t';
const MP4_MIME = 'video/mp4';

/** Tope de bytes descargados. Un clip de 10 min a 2–4 Mbps pesa 150–300 MB. */
export const DEFAULT_MAX_BYTES = 300 * 1024 * 1024;
const DEFAULT_CONCURRENCY = 4;

export interface PlaylistSegment {
  /** URL absoluta del segmento `.ts`. */
  uri: string;
  /** Había un `EXT-X-DISCONTINUITY` justo antes: el reloj del archivo se reinicia. */
  discontinuityBefore: boolean;
  /** `EXT-X-PROGRAM-DATE-TIME` de ESTE segmento, si la playlist lo declara (ms UTC). */
  programDateTimeMs?: number;
}

export interface ClipSegment {
  data: Uint8Array;
  discontinuityBefore: boolean;
  programDateTimeMs?: number;
}

export type ClipDownloadErrorCode = 'EMPTY_PLAYLIST' | 'UNSUPPORTED_FORMAT' | 'TOO_LARGE' | 'ABORTED';

export class ClipDownloadError extends Error {
  readonly code: ClipDownloadErrorCode;
  constructor(code: ClipDownloadErrorCode, message: string) {
    super(message);
    this.name = 'ClipDownloadError';
    this.code = code;
  }
}

/**
 * Lee una playlist HLS VOD y devuelve sus segmentos en orden.
 *
 * `baseUrl` resuelve las rutas relativas; las URLs firmadas de GCS ya son
 * absolutas y se respetan tal cual (no se les toca ni la firma ni el query).
 */
export function parsePlaylist(text: string, baseUrl: string): PlaylistSegment[] {
  const segments: PlaylistSegment[] = [];
  let discontinuityBefore = false;
  let programDateTimeMs: number | undefined;

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    if (line.startsWith('#')) {
      if (line.startsWith('#EXT-X-MAP')) {
        // fMP4 con segmento de inicialización: otro formato, y armar un MP4
        // con él exigiría otra estrategia. Mejor decirlo que emitir basura.
        throw new ClipDownloadError('UNSUPPORTED_FORMAT', 'La playlist usa segmentos fMP4, no MPEG-TS.');
      }
      if (line.startsWith('#EXT-X-DISCONTINUITY') && !line.startsWith('#EXT-X-DISCONTINUITY-SEQUENCE')) {
        discontinuityBefore = true;
      } else if (line.startsWith('#EXT-X-PROGRAM-DATE-TIME:')) {
        const parsed = Date.parse(line.slice('#EXT-X-PROGRAM-DATE-TIME:'.length));
        programDateTimeMs = Number.isNaN(parsed) ? undefined : parsed;
      }
      continue;
    }

    segments.push({ uri: new URL(line, baseUrl).toString(), discontinuityBefore, programDateTimeMs });
    discontinuityBefore = false;
    programDateTimeMs = undefined;
  }

  return segments;
}

export interface MuxTransmuxer {
  on(event: 'data', callback: (segment: { initSegment: Uint8Array; data: Uint8Array }) => void): void;
  push(bytes: Uint8Array): void;
  flush(): void;
}

/** Lo único que este módulo usa de `mux.js` — permite inyectarlo en un test. */
export interface MuxLib {
  mp4: {
    Transmuxer: new (options?: { keepOriginalTimestamps?: boolean; baseMediaDecodeTime?: number }) => MuxTransmuxer;
  };
}

const VIDEO_TIMESCALE = 90_000;

/**
 * Parte los segmentos en tramos continuos, con el desplazamiento (en ticks de
 * 90 kHz) al que cada uno debe empezar en la línea de tiempo del MP4.
 *
 * Solo se parte si TODOS los tramos se pueden colocar en el tiempo real — es
 * decir, si el primer segmento de cada uno trae `EXT-X-PROGRAM-DATE-TIME`.
 * Con eso el hueco de grabación queda como tiempo real entre tramos (un hueco
 * escondido en un video de evidencia sería mentir sobre lo que pasó). Sin ese
 * dato no se puede colocar, y se cae a UN solo tramo continuo.
 */
export function planTracks(segments: ClipSegment[]): { offsetTicks: number; segments: ClipSegment[] }[] {
  const groups: ClipSegment[][] = [];
  for (const segment of segments) {
    if (segment.discontinuityBefore && groups.length > 0) groups.push([segment]);
    else if (groups.length === 0) groups.push([segment]);
    else groups[groups.length - 1].push(segment);
  }

  const placeable = groups.every(group => group[0].programDateTimeMs !== undefined);
  if (groups.length <= 1 || !placeable) return [{ offsetTicks: 0, segments }];

  const origin = groups[0][0].programDateTimeMs as number;
  return groups.map(group => ({
    offsetTicks: Math.max(0, Math.round(((group[0].programDateTimeMs as number) - origin) / 1000 * VIDEO_TIMESCALE)),
    segments: group,
  }));
}

/**
 * MPEG-TS → MP4 fragmentado (init + moof/mdat). Devuelve `null` si `mux.js` no
 * pudo sacar nada (códec no soportado: H.265, audio G.711…), para que quien
 * llama ofrezca el `.ts` en vez de un archivo vacío.
 *
 * Se empuja y vuelca segmento a segmento: el transmuxer conserva el origen de
 * tiempo entre volcados, y la memoria queda acotada por un segmento.
 */
export function remuxToFragmentedMp4(mux: MuxLib, segments: ClipSegment[]): Uint8Array[] | null {
  const output: Uint8Array[] = [];
  let initEmitted = false;

  for (const track of planTracks(segments)) {
    const transmuxer = new mux.mp4.Transmuxer({ keepOriginalTimestamps: false, baseMediaDecodeTime: track.offsetTicks });
    transmuxer.on('data', out => {
      if (!initEmitted) {
        output.push(out.initSegment);
        initEmitted = true;
      }
      output.push(out.data);
    });
    for (const segment of track.segments) {
      transmuxer.push(segment.data);
      transmuxer.flush();
    }
  }

  return initEmitted ? output : null;
}

export interface ClipDownloadDeps {
  fetchPlaylist: (url: string, signal?: AbortSignal) => Promise<string>;
  fetchSegment: (url: string, signal?: AbortSignal) => Promise<Uint8Array>;
  /** Devuelve el MP4 o `null` si no pudo convertir. Puede lanzar: se trata como `null`. */
  remux: (segments: ClipSegment[]) => Promise<Uint8Array[] | null> | Uint8Array[] | null;
}

export type ClipDownloadPhase = 'listing' | 'downloading' | 'converting';

export interface ClipDownloadProgress {
  phase: ClipDownloadPhase;
  done: number;
  total: number;
}

export interface ClipDownloadOptions {
  signal?: AbortSignal;
  onProgress?: (progress: ClipDownloadProgress) => void;
  maxBytes?: number;
  concurrency?: number;
  /**
   * Contra qué se resuelven las rutas relativas (la de la playlist y las de sus
   * segmentos). Por defecto el origen de la página: `playlistUrl` llega como
   * `/v1/...`, sin host.
   */
  baseUrl?: string;
}

export interface ClipDownloadResult {
  blob: Blob;
  extension: 'mp4' | 'ts';
  /** `false` = no se pudo convertir y se entrega el MPEG-TS tal cual. */
  converted: boolean;
  segmentCount: number;
  totalBytes: number;
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new ClipDownloadError('ABORTED', 'Descarga cancelada.');
}

export async function downloadClip(
  playlistUrl: string,
  deps: ClipDownloadDeps,
  options: ClipDownloadOptions = {},
): Promise<ClipDownloadResult> {
  const { signal, onProgress, maxBytes = DEFAULT_MAX_BYTES, concurrency = DEFAULT_CONCURRENCY } = options;
  const baseUrl = options.baseUrl ?? (typeof location !== 'undefined' ? location.href : 'http://localhost/');

  onProgress?.({ phase: 'listing', done: 0, total: 0 });
  assertNotAborted(signal);
  const playlist = await deps.fetchPlaylist(playlistUrl, signal);
  const entries = parsePlaylist(playlist, new URL(playlistUrl, baseUrl).toString());
  if (entries.length === 0) {
    throw new ClipDownloadError('EMPTY_PLAYLIST', 'No hay video grabado en esta ventana.');
  }

  const downloaded = new Array<Uint8Array>(entries.length);
  let next = 0;
  let done = 0;
  let bytes = 0;
  let failure: unknown = null;

  const worker = async (): Promise<void> => {
    while (failure === null) {
      const index = next;
      next += 1;
      if (index >= entries.length) return;
      try {
        assertNotAborted(signal);
        const data = await deps.fetchSegment(entries[index].uri, signal);
        bytes += data.byteLength;
        if (bytes > maxBytes) {
          throw new ClipDownloadError('TOO_LARGE', `El clip supera ${Math.round(maxBytes / 1024 / 1024)} MB: es demasiado grande para armarlo en el navegador.`);
        }
        downloaded[index] = data;
        done += 1;
        onProgress?.({ phase: 'downloading', done, total: entries.length });
      } catch (err) {
        failure = err;
        return;
      }
    }
  };

  onProgress?.({ phase: 'downloading', done: 0, total: entries.length });
  await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, worker));
  if (failure !== null) throw failure;

  const clipSegments: ClipSegment[] = entries.map((entry, index) => ({
    data: downloaded[index],
    discontinuityBefore: entry.discontinuityBefore,
    programDateTimeMs: entry.programDateTimeMs,
  }));

  onProgress?.({ phase: 'converting', done: 0, total: entries.length });
  assertNotAborted(signal);
  let mp4: Uint8Array[] | null = null;
  try {
    mp4 = await deps.remux(clipSegments);
  } catch {
    mp4 = null;
  }

  if (mp4 && mp4.length > 0) {
    return { blob: new Blob(mp4, { type: MP4_MIME }), extension: 'mp4', converted: true, segmentCount: entries.length, totalBytes: bytes };
  }
  return { blob: new Blob(downloaded, { type: MPEG_TS_MIME }), extension: 'ts', converted: false, segmentCount: entries.length, totalBytes: bytes };
}
